import { useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import i18next from "i18next";
import { FIELD_REQUIRED, USE_FB_PAGE_AS_IG } from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useSelectorApp } from "../../lib/redux";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { hasInstagramAccountsConnected } from "../../utils/meta";

const buildInstagramOptionsKey = (instagramAccounts = {}) =>
  Object.keys(instagramAccounts)
    .sort()
    .map((key) => String(instagramAccounts[key]?.id ?? key))
    .join("|");

const ID_OPTION_USE_FB_PAGE_AS_IG = "USE_FB_PAGE_AS_IG";

const SelectInstagramAccount = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  integrationID,
  adAccountID,
  pageID,
  nameUseFBPageAsIG,
  sxTextField = {},
  onChangeAction = () => {},
  isInstagramRequired,
  excludeInstagramIds = [],
  disableAutoSelect = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { setValue, watch, getValues } = useFormContext();
  const optionsKeyRef = useRef("");

  const { isLoading, integrations } = useSelectorApp((state) => state.user);
  const metaIntegrations = integrations?.facebook;
  const integration = metaIntegrations?.[integrationID];

  const hasInstagramAccounts = hasInstagramAccountsConnected(
    integrations,
    integrationID,
    pageID
  );

  const useFBPageAsIGMode =
    !hasInstagramAccounts && !!nameUseFBPageAsIG && !isInstagramRequired;
  const useFBPageAsIG = nameUseFBPageAsIG ? watch(nameUseFBPageAsIG) : false;
  const instagramAccountsForPage =
    integration?.pages?.[pageID]?.instagramAccounts || {};
  const instagramOptionsKey = buildInstagramOptionsKey(
    instagramAccountsForPage
  );

  const specialOption = {
    id: ID_OPTION_USE_FB_PAGE_AS_IG,
    username: i18next.t(USE_FB_PAGE_AS_IG),
    isUseFBPageAsIG: true,
  };
  const displayOptions = useFBPageAsIGMode ? [specialOption] : options;

  useEffect(() => {
    const instagramAccountsKeys = Object.keys(instagramAccountsForPage || {});
    const instagramAccountsData = instagramAccountsKeys.map((key) => ({
      ...instagramAccountsForPage[key],
      integrationID: integration?.id,
    }));
    const excludeSet = new Set((excludeInstagramIds || []).map(String));
    const filtered = instagramAccountsData.filter(
      (a) => !excludeSet.has(String(a.id))
    );
    const nextOptionsKey = filtered.map((a) => String(a.id)).join("|");
    if (nextOptionsKey !== optionsKeyRef.current) {
      optionsKeyRef.current = nextOptionsKey;
      setOptions(filtered);
    }

    const nextId = filtered[0]?.id;
    const currentId = getValues(name);
    if (
      !disableAutoSelect &&
      nextId &&
      String(currentId ?? "") !== String(nextId)
    ) {
      setValue(name, nextId, { shouldDirty: false });
      if (nameUseFBPageAsIG) {
        setValue(nameUseFBPageAsIG, false, { shouldDirty: false });
      }
    }
    // eslint-disable-next-line
  }, [
    instagramOptionsKey,
    integrationID,
    integration?.id,
    adAccountID,
    pageID,
    name,
    nameUseFBPageAsIG,
    setValue,
    getValues,
    excludeInstagramIds,
    disableAutoSelect,
  ]);

  if (isLoading) return null;

  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue={""}
        rules={rules}
        render={({ field }) => {
          const value = useFBPageAsIGMode
            ? useFBPageAsIG
              ? displayOptions[0]
              : null
            : options?.length > 0
              ? field.value
              : "";

          const handleChange = (_, newValue) => {
            if (useFBPageAsIGMode) {
              if (newValue?.id === ID_OPTION_USE_FB_PAGE_AS_IG) {
                setValue(nameUseFBPageAsIG, true);
                field.onChange("");
              } else {
                setValue(nameUseFBPageAsIG, false);
                field.onChange("");
              }
            } else {
              field.onChange(newValue?.id);
              onChangeAction(newValue);
            }
          };

          return (
            <Autocomplete
              {...field}
              hasLabel={Boolean(label)}
              value={value}
              fullWidth={fullWidth}
              disabled={disabled}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              onChange={handleChange}
              isOptionEqualToValue={(option, v) => {
                if (v == null || v === "") return false;
                if (typeof v === "string") return option?.id === v;
                return option?.id === v?.id;
              }}
              getOptionLabel={(option) => {
                if (option == null || option === "") return "";
                const optionValue = options.find((opt) => opt.id === option);
                if (optionValue) {
                  return `${optionValue?.username}`;
                }
                if (typeof option === "string") {
                  return option;
                }
                return option?.username || "";
              }}
              disableClearable={!useFBPageAsIGMode}
              options={displayOptions}
              renderOption={(props, option) => (
                <Box
                  {...props}
                  component="li"
                  key={option.id}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {option?.isUseFBPageAsIG ? (
                    <Typography variant="body1">{option.username}</Typography>
                  ) : (
                    <>
                      <Avatar
                        src={option.imageURL}
                        alt={option.username}
                        sx={{ width: 30, height: 30 }}
                      />
                      <Typography variant="body1">{option.username}</Typography>
                    </>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  hiddenLabel={!label}
                  label={label}
                  variant={variant}
                  placeholder={
                    useFBPageAsIGMode
                      ? !useFBPageAsIG
                        ? placeholder
                        : ""
                      : field?.value?.length > 0
                        ? ""
                        : placeholder
                  }
                  error={!!error}
                  sx={{
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    ...sxTextField,
                  }}
                />
              )}
            />
          );
        }}
      />
      {(error?.message || helperText) && (
        <Typography
          variant="caption"
          color={error?.message ? "error" : "fields.placeholder"}
        >
          {error?.message || helperText}
        </Typography>
      )}
    </>
  );
};

export default SelectInstagramAccount;
